import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Reply, CheckCircle2, RotateCcw, MessagesSquare, Trash2 } from 'lucide-react';
import { buildContactReply, buildQuoteReply } from './inboxTemplates';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso || '';
  }
};

const ThreadBadge = ({ count, onClick }) => {
  if (count < 1) return null;
  const label = count > 1 ? `${count} total interactions with this email` : 'Open thread / add note';
  return (
    <Button
      size="sm" variant="ghost"
      onClick={onClick}
      data-testid="thread-badge"
      className="h-6 px-2 text-blue-400 hover:bg-blue-950/40 hover:text-blue-300"
      title={label}
    >
      <MessagesSquare className="w-3 h-3 mr-1" /> {count > 1 ? count : ''}
    </Button>
  );
};

const isReplied = (r) => r.status === 'replied';

// Header checkbox: checked when all visible rows are selected; indeterminate when some are.
const SelectAllCell = ({ rows, selectedIds, onToggleAll, testid }) => {
  const total = rows.length;
  const selectedVisible = rows.filter((r) => selectedIds.has(r.id)).length;
  const allSelected = total > 0 && selectedVisible === total;
  const someSelected = selectedVisible > 0 && selectedVisible < total;
  // Radix Checkbox supports an "indeterminate" state via the `checked` prop
  const checkedValue = allSelected ? true : someSelected ? 'indeterminate' : false;
  return (
    <Checkbox
      data-testid={testid}
      checked={checkedValue}
      onCheckedChange={(v) => onToggleAll(Boolean(v))}
      disabled={total === 0}
      aria-label="Select all rows"
    />
  );
};

const RowSelectCell = ({ id, selectedIds, onToggleOne, testid }) => (
  <Checkbox
    data-testid={testid}
    checked={selectedIds.has(id)}
    onCheckedChange={(v) => onToggleOne(id, Boolean(v))}
    aria-label="Select row"
  />
);

const ContactsTable = ({
  rows, onMarkReplied, onUnmark, onDelete, threadCounts, onOpenThread,
  selectedIds, onToggleOne, onToggleAll,
}) => (
  <div className="overflow-x-auto rounded border border-slate-800" data-testid="contacts-table">
    <table className="w-full text-sm">
      <thead className="bg-slate-900 text-gray-400 text-left">
        <tr>
          <th className="px-3 py-2 font-medium w-10">
            <SelectAllCell rows={rows} selectedIds={selectedIds} onToggleAll={onToggleAll} testid="contacts-select-all" />
          </th>
          <th className="px-3 py-2 font-medium w-8"></th>
          <th className="px-3 py-2 font-medium">Date</th>
          <th className="px-3 py-2 font-medium">Name</th>
          <th className="px-3 py-2 font-medium">Email</th>
          <th className="px-3 py-2 font-medium">Phone</th>
          <th className="px-3 py-2 font-medium">Category</th>
          <th className="px-3 py-2 font-medium">Message</th>
          <th className="px-3 py-2 font-medium text-right">Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">No contacts in this window.</td></tr>
        ) : rows.map((r) => (
          <tr key={r.id} className={`border-t border-slate-800 hover:bg-slate-900/70 align-top ${isReplied(r) ? 'opacity-70' : ''} ${selectedIds.has(r.id) ? 'bg-slate-900/60' : ''}`} data-testid={`contact-row-${r.id}`}>
            <td className="px-3 py-2">
              <RowSelectCell id={r.id} selectedIds={selectedIds} onToggleOne={onToggleOne} testid={`contact-select-${r.id}`} />
            </td>
            <td className="px-3 py-2 text-center">
              {isReplied(r)
                ? <CheckCircle2 className="w-4 h-4 text-green-500 inline" data-testid={`contact-replied-${r.id}`} />
                : <span className="w-2 h-2 bg-red-500 rounded-full inline-block" data-testid={`contact-new-${r.id}`} title="Unreplied" />}
            </td>
            <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(r.created_at)}</td>
            <td className="px-3 py-2 text-white font-medium whitespace-nowrap">
              <span className="inline-flex items-center gap-2">
                {r.name}
                <ThreadBadge
                  count={threadCounts?.[r.email?.toLowerCase()] || 0}
                  onClick={() => onOpenThread?.(r.email)}
                />
              </span>
            </td>
            <td className="px-3 py-2"><a href={`mailto:${r.email}`} className="text-red-400 hover:underline">{r.email}</a></td>
            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{r.phone || '—'}</td>
            <td className="px-3 py-2">
              {r.category
                ? <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30">{r.category}</Badge>
                : <span className="text-gray-500">—</span>}
            </td>
            <td className="px-3 py-2 text-gray-300 max-w-md">
              {r.subject && (
                <div className="text-white font-medium text-xs mb-0.5" data-testid={`contact-subject-${r.id}`}>{r.subject}</div>
              )}
              <p className="line-clamp-2">{r.message}</p>
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap">
              <div className="inline-flex items-center gap-1 justify-end">
                {isReplied(r) ? (
                  <Button
                    size="sm" variant="outline"
                    data-testid={`contact-unmark-${r.id}`}
                    onClick={() => onUnmark(r)}
                    className="border-slate-700 text-gray-400 hover:bg-slate-800"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Mark unread
                  </Button>
                ) : (
                  <a
                    href={buildContactReply(r)}
                    data-testid={`contact-reply-${r.id}`}
                    onClick={() => onMarkReplied(r)}
                  >
                    <Button size="sm" variant="outline" className="border-red-900/60 text-red-400 hover:bg-red-950/40">
                      <Reply className="w-3 h-3 mr-1" /> Reply
                    </Button>
                  </a>
                )}
                <Button
                  size="sm" variant="outline"
                  data-testid={`contact-delete-${r.id}`}
                  onClick={() => onDelete?.(r)}
                  title="Delete this message"
                  aria-label="Delete contact"
                  className="border-slate-700 text-gray-400 hover:bg-red-950/40 hover:text-red-400 hover:border-red-900/60"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const QuotesTable = ({
  rows, onMarkReplied, onUnmark, onDelete, threadCounts, onOpenThread,
  selectedIds, onToggleOne, onToggleAll,
}) => (
  <div className="overflow-x-auto rounded border border-slate-800" data-testid="quotes-table">
    <table className="w-full text-sm">
      <thead className="bg-slate-900 text-gray-400 text-left">
        <tr>
          <th className="px-3 py-2 font-medium w-10">
            <SelectAllCell rows={rows} selectedIds={selectedIds} onToggleAll={onToggleAll} testid="quotes-select-all" />
          </th>
          <th className="px-3 py-2 font-medium w-8"></th>
          <th className="px-3 py-2 font-medium">Date</th>
          <th className="px-3 py-2 font-medium">Name</th>
          <th className="px-3 py-2 font-medium">Email</th>
          <th className="px-3 py-2 font-medium">Phone</th>
          <th className="px-3 py-2 font-medium">Service</th>
          <th className="px-3 py-2 font-medium">Firearm</th>
          <th className="px-3 py-2 font-medium">Timeline</th>
          <th className="px-3 py-2 font-medium">Description</th>
          <th className="px-3 py-2 font-medium text-right">Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={11} className="px-3 py-6 text-center text-gray-500">No quotes in this window.</td></tr>
        ) : rows.map((r) => (
          <tr key={r.id} className={`border-t border-slate-800 hover:bg-slate-900/70 align-top ${isReplied(r) ? 'opacity-70' : ''} ${selectedIds.has(r.id) ? 'bg-slate-900/60' : ''}`} data-testid={`quote-row-${r.id}`}>
            <td className="px-3 py-2">
              <RowSelectCell id={r.id} selectedIds={selectedIds} onToggleOne={onToggleOne} testid={`quote-select-${r.id}`} />
            </td>
            <td className="px-3 py-2 text-center">
              {isReplied(r)
                ? <CheckCircle2 className="w-4 h-4 text-green-500 inline" data-testid={`quote-replied-${r.id}`} />
                : <span className="w-2 h-2 bg-red-500 rounded-full inline-block" data-testid={`quote-new-${r.id}`} title="Unreplied" />}
            </td>
            <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(r.created_at)}</td>
            <td className="px-3 py-2 text-white font-medium whitespace-nowrap">
              <span className="inline-flex items-center gap-2">
                {r.name}
                <ThreadBadge
                  count={threadCounts?.[r.email?.toLowerCase()] || 0}
                  onClick={() => onOpenThread?.(r.email)}
                />
              </span>
            </td>
            <td className="px-3 py-2"><a href={`mailto:${r.email}`} className="text-red-400 hover:underline">{r.email}</a></td>
            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{r.phone || '—'}</td>
            <td className="px-3 py-2">
              <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30">{r.service_type}</Badge>
            </td>
            <td className="px-3 py-2 text-gray-300">{r.firearm_type || '—'}</td>
            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{r.timeline || '—'}</td>
            <td className="px-3 py-2 text-gray-300 max-w-md">
              <p className="line-clamp-2">{r.description}</p>
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap">
              <div className="inline-flex items-center gap-1 justify-end">
                {isReplied(r) ? (
                  <Button
                    size="sm" variant="outline"
                    data-testid={`quote-unmark-${r.id}`}
                    onClick={() => onUnmark(r)}
                    className="border-slate-700 text-gray-400 hover:bg-slate-800"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Mark unread
                  </Button>
                ) : (
                  <a
                    href={buildQuoteReply(r)}
                    data-testid={`quote-reply-${r.id}`}
                    onClick={() => onMarkReplied(r)}
                  >
                    <Button size="sm" variant="outline" className="border-red-900/60 text-red-400 hover:bg-red-950/40">
                      <Reply className="w-3 h-3 mr-1" /> Reply
                    </Button>
                  </a>
                )}
                <Button
                  size="sm" variant="outline"
                  data-testid={`quote-delete-${r.id}`}
                  onClick={() => onDelete?.(r)}
                  title="Delete this quote"
                  aria-label="Delete quote"
                  className="border-slate-700 text-gray-400 hover:bg-red-950/40 hover:text-red-400 hover:border-red-900/60"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export { ContactsTable, QuotesTable };
